import { Role, useGetAccessTokenPayload, useGetUserRoles } from 'auth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewHolderOnboardedModal } from '@/modules/auth/components/NewHolderOnboardedModal.tsx';
import { NEW_HOLDER_ONBOARDED_ATTRIBUTE_KEY } from '@/modules/auth/constants/onboardingNotifications.ts';
import { useClearNewHolderOnboardedNotifications } from '@/modules/common/queries/organizations.ts';
import {
  HOLDERS_TAB_NAME,
  HoldersTabNames,
} from '@/modules/issuer-dashboard/pages/IssuerHolders.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';

interface OrganizationRoleEntry {
  roles?: string[];
}

const isTruthyAttribute = (value?: string) =>
  !!value && !['false', '0', ''].includes(value.toLowerCase());

export function NewHolderOnboardedPopupController() {
  const tokenPayload = useGetAccessTokenPayload();
  const userRoles = useGetUserRoles('sr');
  const navigate = useNavigate();
  const [dismissedKey, setDismissedKey] = useState('');
  const clearNotifications = useClearNewHolderOnboardedNotifications();

  const pendingOrganizationIds = useMemo(() => {
    if (!userRoles.includes(Role.SR_ISSUER_ADMIN)) {
      return [];
    }

    const organizationRoles = tokenPayload?.organization_roles || {};
    const organizationAttributes = tokenPayload?.organization_attribute || {};

    return Object.entries(organizationRoles).reduce<string[]>((acc, [organizationId, data]) => {
      const roles = Array.isArray((data as OrganizationRoleEntry)?.roles)
        ? (data as OrganizationRoleEntry).roles ?? []
        : [];

      if (!roles.includes(Role.SR_ISSUER_ADMIN)) {
        return acc;
      }

      const attributeValue =
        organizationAttributes?.[organizationId]?.attributes?.[
          NEW_HOLDER_ONBOARDED_ATTRIBUTE_KEY
        ]?.[0];

      return isTruthyAttribute(attributeValue) ? [...acc, organizationId] : acc;
    }, []);
  }, [tokenPayload?.organization_attribute, tokenPayload?.organization_roles, userRoles]);

  const pendingOrganizationsKey = useMemo(
    () => pendingOrganizationIds.slice().sort().join(','),
    [pendingOrganizationIds],
  );

  const open = !!pendingOrganizationsKey && pendingOrganizationsKey !== dismissedKey;

  useEffect(() => {
    if (!pendingOrganizationsKey) {
      setDismissedKey('');
    }
  }, [pendingOrganizationsKey]);

  const handleClose = () => {
    setDismissedKey(pendingOrganizationsKey);

    if (!pendingOrganizationIds.length) {
      return;
    }

    clearNotifications.mutate();
  };

  const handleViewHolders = () => {
    handleClose();
    const nextParams = new URLSearchParams(location.search);
    nextParams.set(HOLDERS_TAB_NAME, HoldersTabNames.PendingHolders);

    navigate({
      pathname: issuerDashboardPaths.static.holders,
      search: nextParams.toString(),
    });
  };

  return (
    <NewHolderOnboardedModal
      open={open}
      onViewHolders={handleViewHolders}
      isLoading={clearNotifications.isPending}
    />
  );
}
